import type { AttachmentRef } from "../../core/attachments";
import type { EditorDocument } from "../../core/types";

// How a message is sent. None of it is about Lexical, so this file reaches none: it is given the
// draft, the edit sequence, and a way to clear, and is tested without an editor.

/**
 * What the user is about to send. The editor builds these objects and the host only reads them, so
 * fields can be added later without breaking anyone, as `attachments` was.
 */
export interface ComposerMessage {
  /** An immutable snapshot of the draft. */
  document: EditorDocument;
  plainText: string;
  /**
   * The files given to this message, in the order they were given. They go beside the message,
   * not in it: `document` holds none of them. Each is kept by the host's store already; whether
   * it has been uploaded by now is the store's to say, and the host's to wait for or not. Empty
   * in a composer that was given no store.
   */
  attachments: readonly AttachmentRef[];
}

export interface ComposerSubmission extends ComposerMessage {
  /**
   * Stable for this message, so the host can make delivery idempotent: its own retries carry it,
   * and so does the user sending the same unchanged draft again after a failure. A draft that was
   * edited in between is a new message with a new key.
   */
  idempotencyKey: string;
}

/**
 * The default empty-message policy: no text but whitespace, and no files. An empty list item is
 * blank too; a message that is only a file is not.
 */
export const isBlankMessage = (message: ComposerMessage): boolean =>
  message.plainText.trim() === "" && message.attachments.length === 0;

export type SubmitOutcome =
  | "sent"
  | "not-offered"
  | "empty-message"
  | "send-pending"
  /** A file that was given is still being kept by the host's store. Sending now would leave it behind. */
  | "attachments-pending";

/** Whether a send is waiting for the host, for the controls that show it. */
export interface PendingStore {
  get(): boolean;
  set(pending: boolean): void;
  subscribe(listener: () => void): () => void;
}

export function createPendingStore(): PendingStore {
  let pending = false;
  const listeners = new Set<() => void>();
  return {
    get: () => pending,
    set(next) {
      if (next === pending) return;
      pending = next;
      for (const listener of listeners) listener();
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

/** As much of the composer's attachment tray as sending needs. */
export interface SubmitTray {
  getState(): { staging: number };
  getRevision(): number;
  remove(assetIds: readonly string[]): void;
}

export interface ComposerSubmitOptions {
  pending: PendingStore;
  message(): ComposerMessage;
  getEditSequence(): number;
  /** The composer's attachments, where it has any. */
  tray: SubmitTray | null;
  /**
   * Empties the editor for the next draft, whose first block gets an ID of its own, and drops
   * history. Called only when nothing was typed since the send.
   */
  clearDraft(): void;
  /** Read at each send, so the host's callbacks can change without rebuilding anything. */
  callbacks(): {
    onSubmit: ((submission: ComposerSubmission) => void | Promise<void>) | undefined;
    isEmptyMessage: ((message: ComposerMessage) => boolean) | undefined;
    onError: ((error: Error) => void) | undefined;
  };
}

/**
 * The one way a message is sent: Enter, the send button, and the `submit` intent all end here. A
 * refusal happens before the host is called and changes nothing.
 */
export function createComposerSubmit(options: ComposerSubmitOptions): () => SubmitOutcome {
  const { pending, getEditSequence, tray } = options;
  // The key of the last send that failed, and what the draft was then: the edit sequence of its
  // text and the revision of its attachments. The host may have delivered that message and lost
  // the answer, so the same draft goes out under the same key, and any other under a new one.
  let failed: { sequence: number; tray: number; key: string } | null = null;

  const clear = (sentAt: number): void => {
    // Anything typed since the send is newer than what the host acknowledged: keep it.
    if (getEditSequence() === sentAt) options.clearDraft();
  };

  return () => {
    const { onSubmit, isEmptyMessage = isBlankMessage, onError } = options.callbacks();
    if (onSubmit === undefined) return "not-offered";
    if (pending.get()) return "send-pending";
    if ((tray?.getState().staging ?? 0) > 0) return "attachments-pending";
    const message = options.message();
    if (isEmptyMessage(message)) return "empty-message";

    const sentAt = getEditSequence();
    const trayAt = tray?.getRevision() ?? 0;
    const idempotencyKey =
      failed?.sequence === sentAt && failed.tray === trayAt ? failed.key : crypto.randomUUID();
    const fail = (): void => {
      failed = { sequence: sentAt, tray: trayAt, key: idempotencyKey };
    };
    // Exactly what went out, whatever is added while the host is answering: that is newer than
    // what it acknowledges, as text typed meanwhile is.
    const sent = message.attachments.map((attachment) => attachment.assetId);
    const acknowledged = (): void => {
      failed = null;
      tray?.remove(sent);
      clear(sentAt);
    };
    let result: void | Promise<void>;
    try {
      result = onSubmit({ ...message, idempotencyKey });
    } catch (error) {
      // A host that throws did not take the message. Its draft stays, like any failed send.
      fail();
      onError?.(error instanceof Error ? error : new Error(String(error)));
      return "sent";
    }
    if (result === undefined) {
      acknowledged();
      return "sent";
    }
    pending.set(true);
    result.then(acknowledged, fail).finally(() => pending.set(false));
    return "sent";
  };
}
